import { useState, useCallback } from 'react';
import { Message } from '@/types/chat';

export function useChatUI() {
  // Check if user has cached conversations to determine initial sidebar state
  // This prevents the flash of centered input on desktop when user has existing chats
  const getInitialSidebarState = () => {
    if (typeof window === 'undefined') return false; // SSR

    // Only auto-open on desktop (wider screens)
    const isDesktop = window.innerWidth >= 768; // md breakpoint
    if (!isDesktop) return false;

    // Check if user has any cached conversations
    try {
      const keys = Object.keys(localStorage);
      const hasCachedConversations = keys.some(key =>
        key.startsWith('dify-conversations-') &&
        localStorage.getItem(key) !== '[]'
      );
      return hasCachedConversations;
    } catch {
      return false;
    }
  };

  // UI State
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(getInitialSidebarState);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [inputMessage, setInputMessage] = useState<string>('');
  const [messageHoverStates, setMessageHoverStates] = useState<Record<string, boolean>>({});

  // Reply State
  const [replyingToMessage, setReplyingToMessage] = useState<Message | null>(null);

  // UI Handlers
  const toggleSidebar = useCallback(() => setIsSidebarOpen(prev => !prev), []);
  const openSidebar = useCallback(() => setIsSidebarOpen(true), []);
  const closeSidebar = useCallback(() => setIsSidebarOpen(false), []);

  const handleMessageHover = useCallback((messageId: string, isHovering: boolean) => {
    setMessageHoverStates(prev => ({ ...prev, [messageId]: isHovering }));
  }, []);

  // Reply Handlers
  const handleReplyToMessage = useCallback((message: Message) => {
    // This will need to be coordinated with useMessageEditing's handleCancelEdit
    setReplyingToMessage(message);
  }, []);

  const cancelReply = useCallback(() => {
    setReplyingToMessage(null);
  }, []);

  return {
    isSidebarOpen,
    toggleSidebar,
    openSidebar,
    closeSidebar,
    searchTerm,
    setSearchTerm,
    inputMessage,
    setInputMessage,
    messageHoverStates,
    handleMessageHover,
    replyingToMessage,
    handleReplyToMessage,
    cancelReply,
  };
}
